import { Users } from '@just-in-types';

export const cleanBoolean = (value: any): value is boolean => {
  return typeof value === 'boolean' ? value : false;
};

export const cleanString = (value: any) =>
  typeof value === 'string' ? value : null;

export const cleanLanguage = (value: any): Users.User['language'] =>
  value === 'eng' || value === 'esp' ? value : 'eng';

export const cleanPhoneNumber = (value: string): string => {
  return value.slice(2);
};

const obscureMobile = (value: string) => {
  const valueArr = value.split('');
  valueArr[4] = '*';
  valueArr[5] = '*';
  valueArr[6] = '*';
  return valueArr.join('');
};

export const cleanMobile = (
  value: any,
): { mobile: null | string; secretMobile: null | string } => {
  if (typeof value === 'string') {
    if (value.length === 10) {
      return { mobile: cleanString(value), secretMobile: obscureMobile(value) };
    } else if (value.length === 12) {
      return {
        mobile: cleanPhoneNumber(value),
        secretMobile: obscureMobile(value),
      };
    }
  }

  return { mobile: null, secretMobile: null };
};

export const cleanName = (
  name:
    | string
    | undefined
    | {
        displayName: null | string | undefined;
        firstname: null | string | undefined;
        lastname: null | string | undefined;
      },
): Users.User['name'] => {
  const nameObject: Users.User['name'] = {
    displayName: null,
    firstname: null,
    lastname: null,
  };

  if (name) {
    if (typeof name === 'string') {
      nameObject.firstname = name.split(' ')[0];
      nameObject.lastname = name.split(' ')[1];
    } else {
      if (name.firstname) {
        nameObject.firstname = name.firstname;
      }
      if (name.lastname) {
        nameObject.lastname = name.lastname;
      }
    }
  }

  return nameObject;
};

export const cleanRole = (
  role: string | undefined,
): Users.AccountData['permissions']['role'] => {
  let roleString: null | Users.AccountData['permissions']['role'] = null;

  if (role) {
    if (role === 'admin' || role === 'owner') {
      roleString = role;
    } else {
      roleString = 'user';
    }
  } else {
    roleString = 'user';
  }

  return roleString;
};

export const cleanVehicles = (vehicles: any[]): Users.VehiclePick[] => {
  if (Array.isArray(vehicles)) {
    const vehicleArray: Users.VehiclePick[] = [];

    for (const vehicle of vehicles) {
      const clean = cleanVehicle(vehicle);

      if (clean) {
        vehicleArray.push(clean);
      }
    }

    return vehicleArray;
  } else {
    return [];
  }
};

export const cleanVehicle = (vehicle: any): null | Users.VehiclePick => {
  const vehicleObject: {
    businessId: null | string;
    make: null | string;
    model: null | string;
    siteId: null | string;
    yearMax: null | number;
    yearMin: null | number;
  } = {
    businessId: null,
    make: null,
    model: null,
    siteId: null,
    yearMax: null,
    yearMin: null,
  };

  if (!!vehicle && typeof vehicle === 'object') {
    if (vehicle.businessId && isBusinessId(vehicle.businessId)) {
      vehicleObject.businessId = vehicle.businessId;
    }
    if (vehicle.make && isMake(vehicle.make)) {
      vehicleObject.make = vehicle.make;
    }
    if (vehicle.model && isModel(vehicle.model)) {
      vehicleObject.model = vehicle.model;
    }
    if (vehicle.siteId && isSiteId(vehicle.siteId)) {
      vehicleObject.siteId = vehicle.siteId;
    }
    if ('year' in vehicle && vehicle.year && typeof vehicle.year === 'number') {
      vehicleObject.yearMax = vehicle.year;
      vehicleObject.yearMin = vehicle.year;
    } else {
      if (vehicle.yearMax && isYearMax(vehicle.yearMax)) {
        vehicleObject.yearMax = vehicle.yearMax;
      }
      if (vehicle.yearMin && isYearMin(vehicle.yearMin)) {
        vehicleObject.yearMin = vehicle.yearMin;
      }
    }

    if (
      vehicleObject.businessId !== null &&
      vehicleObject.make !== null &&
      vehicleObject.model !== null &&
      vehicleObject.siteId !== null &&
      vehicleObject.yearMax !== null &&
      vehicleObject.yearMin !== null
    ) {
      return {
        businessId: vehicleObject.businessId,
        make: vehicleObject.make,
        model: vehicleObject.model,
        siteId: vehicleObject.siteId,
        yearMax: vehicleObject.yearMax,
        yearMin: vehicleObject.yearMin,
      };
    } else {
      return null;
    }
  } else {
    return null;
  }
};

type BusinessId = Users.VehiclePick['businessId'];
type Make = Users.VehiclePick['make'];
type Model = Users.VehiclePick['model'];
type SiteId = Users.VehiclePick['siteId'];
type YearMax = Users.VehiclePick['yearMax'];
type YearMin = Users.VehiclePick['yearMin'];

function isBusinessId(value: any): value is BusinessId {
  return (value as BusinessId).anchor !== null;
}
function isMake(value: any): value is Make {
  return (value as Make).anchor !== null;
}
function isModel(value: any): value is Model {
  return (value as Model).anchor !== null;
}
function isSiteId(value: any): value is SiteId {
  return (value as SiteId).anchor !== null;
}
function isYearMax(value: any): value is YearMax {
  return (value as YearMax).toExponential !== null;
}
function isYearMin(value: any): value is YearMin {
  return (value as YearMin).toExponential !== null;
}



// WEBPACK FOOTER //
// ./src/adminFunctions/cleaningFunctions.ts